import { useEffect, useState } from "react";
import { ArrowPathIcon, ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { classNames } from "../../../ui/styles/classNames";
import { readStoredStringSet, writeStoredStringSet } from "../../../shared/storage/storedStringSet";
import type { OntologyEntity } from "./types";
import { entityDisplayName } from "./types";
import type { OntologyRelation } from "./relationTree";
import { chipClassName } from "../../../ui/styles/chipStyles";
import AppButton from "../../../ui/buttons/AppButton";

interface OntologyRelationTreeProps {
  rootEntities: OntologyEntity[];
  entitiesByIri: Map<string, OntologyEntity>;
  relationsBySource: Map<string, OntologyRelation[]>;
  selectedIri: string | null;
  storageKey: string;
  onSelect: (iri: string) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

function loadExpanded(storageKey: string): Set<string> {
  return readStoredStringSet(storageKey) ?? new Set();
}

function relationPathKey(path: string[]): string {
  return JSON.stringify(path);
}

function relationStepKey(relation: OntologyRelation, index: number): string {
  return `${relation.propertyIri}->${relation.targetIri}#${index}`;
}

export default function OntologyRelationTree({
  rootEntities,
  entitiesByIri,
  relationsBySource,
  selectedIri,
  storageKey,
  onSelect,
  t,
}: OntologyRelationTreeProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => loadExpanded(storageKey));
  const [loadedStorageKey, setLoadedStorageKey] = useState(storageKey);
  if (loadedStorageKey !== storageKey) {
    setLoadedStorageKey(storageKey);
    setExpanded(loadExpanded(storageKey));
  }

  useEffect(() => {
    writeStoredStringSet(storageKey, expanded);
  }, [expanded, storageKey]);

  const toggle = (pathKey: string) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(pathKey)) {
        next.delete(pathKey);
      } else {
        next.add(pathKey);
      }
      return next;
    });
  };

  const renderEntityButton = (entity: OntologyEntity, isSelected: boolean, isCycle = false) => {
    const label = entityDisplayName(entity);

    return (
      <button
        type="button"
        className={classNames(
          "min-w-0 rounded px-2 py-1 text-left text-sm",
          isSelected ? "font-medium" : "app-action-link"
        )}
        style={isSelected ? { background: "var(--app-accent-soft)", color: "var(--app-text)" } : undefined}
        title={entity.iri}
        onClick={() => { onSelect(entity.iri); }}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate">{label}</span>
          {isCycle ? (
            <span
              className={classNames(chipClassName(), "h-5 w-5 flex-shrink-0 justify-center !p-0")}
              title={t("ontologyViewer.cycleReference")}
              aria-label={t("ontologyViewer.cycleReference")}
            >
              <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
          ) : null}
        </span>
      </button>
    );
  };

  const renderNode = (iri: string, depth: number, path: string[]) => {
    const entity = entitiesByIri.get(iri);
    if (!entity) {
      return null;
    }

    const label = entityDisplayName(entity);
    const currentPathKey = relationPathKey(path);
    const isCycle = path.slice(0, -1).includes(iri);
    const outgoingRelations = relationsBySource.get(iri) || [];
    const hasChildren = !isCycle && outgoingRelations.length > 0;
    const isExpanded = expanded.has(currentPathKey);
    const isSelected = selectedIri === iri;

    return (
      <li key={currentPathKey}>
        <div className="flex min-h-[32px] items-center gap-1" style={{ paddingLeft: `${depth * 22}px` }}>
          {hasChildren ? (
            <AppButton
              variant="ghost"
              size="xs"
              className="h-6 w-6 p-0"
              onClick={() => { toggle(currentPathKey); }}
              aria-label={
                isExpanded
                  ? t("ontologyViewer.collapseEntity", { name: label })
                  : t("ontologyViewer.expandEntity", { name: label })
              }
            >
              {isExpanded ? (
                <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
              )}
            </AppButton>
          ) : (
            <span className="h-6 w-6 flex-shrink-0" />
          )}
          {renderEntityButton(entity, isSelected, isCycle)}
        </div>
        {hasChildren && isExpanded ? (
          <ul>
            {outgoingRelations.map((relation, index) =>
              renderRelation(relation, index, depth + 1, path)
            )}
          </ul>
        ) : null}
      </li>
    );
  };

  const renderRelation = (
    relation: OntologyRelation,
    index: number,
    depth: number,
    sourcePath: string[]
  ) => {
    const target = entitiesByIri.get(relation.targetIri);
    const property = entitiesByIri.get(relation.propertyIri);
    if (!target || !property) {
      return null;
    }

    const propertyLabel = entityDisplayName(property);
    const targetLabel = entityDisplayName(target);
    const nextPath = [...sourcePath, relationStepKey(relation, index), relation.targetIri];
    const currentPathKey = relationPathKey(nextPath);
    const isCycle = sourcePath.includes(relation.targetIri);
    const outgoingRelations = relationsBySource.get(relation.targetIri) || [];
    const hasChildren = !isCycle && outgoingRelations.length > 0;
    const isExpanded = expanded.has(currentPathKey);
    const isSelected = selectedIri === relation.targetIri;

    return (
      <li key={currentPathKey}>
        <div className="flex min-h-[32px] items-center gap-1" style={{ paddingLeft: `${depth * 22}px` }}>
          {hasChildren ? (
            <AppButton
              variant="ghost"
              size="xs"
              className="h-6 w-6 p-0"
              onClick={() => { toggle(currentPathKey); }}
              aria-label={
                isExpanded
                  ? t("ontologyViewer.collapseEntity", { name: targetLabel })
                  : t("ontologyViewer.expandEntity", { name: targetLabel })
              }
            >
              {isExpanded ? (
                <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
              )}
            </AppButton>
          ) : (
            <span className="h-6 w-6 flex-shrink-0" />
          )}
          <span
            className={classNames(chipClassName(), "max-w-[11rem] flex-shrink")}
            title={propertyLabel}
            aria-label={t("ontologyViewer.relationProperty", { name: propertyLabel })}
          >
            <span className="truncate">{propertyLabel}</span>
          </span>
          
          <span className="app-text-muted flex-shrink-0 font-mono text-xs" aria-hidden="true">
            
          </span>
          <div className="min-w-0 flex-1">
            {renderEntityButton(target, isSelected, isCycle)}
          </div>
        </div>
        {hasChildren && isExpanded ? (
          <ul>
            {outgoingRelations.map((childRelation, childIndex) =>
              renderRelation(childRelation, childIndex, depth + 1, nextPath)
            )}
          </ul>
        ) : null}
      </li>
    );
  };

  if (rootEntities.length === 0) {
    return <p className="app-text-muted p-3 text-sm">{t("ontologyViewer.noEntities")}</p>;
  }

  return (
    <ul className="space-y-0.5">
      {rootEntities.map((entity) => renderNode(entity.iri, 0, [entity.iri]))}
    </ul>
  );
}
